import { inject, Type } from '@angular/core';
import { Route, Routes } from '@angular/router';
import {
  maintenanceGuard,
  pageGuard,
  projectTypeGuard,
  retailerRedirectionGuard,
} from '@core/guards';
import { DataService } from '@core/services/data.service';
import { Pages } from '@shared/constants';
import { ProjectTypes } from '@shared/models';

import { AuthComponent } from '../pages/auth/auth.component';
import { ChallengePreviewComponent } from '../pages/challenge-preview/challenge-preview.component';
import { CookiePolicyComponent } from '../pages/cookie-policy/cookie-policy.component';
import { HelpComponent } from '../pages/help/help.component';
import { MaintenanceComponent } from '../pages/maintenance/maintenance.component';
import { SorryComponent } from '../pages/sorry/sorry.component';

export const APP_ROUTES: Route[] = [
  {
    path: '',
    pathMatch: 'full',
    canMatch: [maintenanceGuard, retailerRedirectionGuard],
    loadComponent: () => import('./app.component').then((m) => m.AppComponent),
  },
  {
    path: Pages.LOGIN,
    canMatch: [maintenanceGuard, pageGuard],
    loadComponent: () =>
      import('../pages/auth/auth.component').then(
        (m: { AuthComponent: Type<AuthComponent> }) => m.AuthComponent
      ),
  },
  {
    // The coupons help page always needs an `untienotsId` (it links back to
    // the coupons page), so only challenges expose it unlogged.
    path: Pages.HELP,
    data: { type: ProjectTypes.LOYALTY_CHALLENGE },
    canMatch: [maintenanceGuard, pageGuard, projectTypeGuard],
    loadComponent: () =>
      import('../pages/help/help.component').then(
        (m: { HelpComponent: Type<HelpComponent> }) => m.HelpComponent
      ),
  },
  {
    path: 'challengefid/fr/login',
    canMatch: [maintenanceGuard, pageGuard],
    loadComponent: () =>
      import('../pages/auth/auth.component').then(
        (m: { AuthComponent: Type<AuthComponent> }) => m.AuthComponent
      ),
  },
  {
    path: Pages.SORRY,
    canMatch: [maintenanceGuard],
    loadComponent: () =>
      import('../pages/sorry/sorry.component').then(
        (m: { SorryComponent: Type<SorryComponent> }) => m.SorryComponent
      ),
  },
  {
    path: Pages.MAINTENANCE,
    canMatch: [maintenanceGuard],
    loadComponent: () =>
      import('../pages/maintenance/maintenance.component').then(
        (m: { MaintenanceComponent: Type<MaintenanceComponent> }) =>
          m.MaintenanceComponent
      ),
  },
  {
    path: Pages.COOKIE_POLICY,
    canMatch: [maintenanceGuard, pageGuard],
    loadComponent: () =>
      import('../pages/cookie-policy/cookie-policy.component').then(
        (m: { CookiePolicyComponent: Type<CookiePolicyComponent> }) =>
          m.CookiePolicyComponent
      ),
  },
  {
    path: Pages.CHALLENGE_PREVIEW,
    loadComponent: () =>
      import('../pages/challenge-preview/challenge-preview.component').then(
        (m: { ChallengePreviewComponent: Type<ChallengePreviewComponent> }) =>
          m.ChallengePreviewComponent
      ),
  },
  {
    path: ':untienotsId',
    canMatch: [maintenanceGuard],
    loadChildren: () =>
      import('../pages/layout/layout.routes').then(
        (m: { LAYOUT_ROUTES: Routes }) => m.LAYOUT_ROUTES
      ),
  },
  {
    path: '**',
    pathMatch: 'full',
    redirectTo: () => {
      const availablePages = inject(DataService).config().pages.availablePages;
      if (availablePages.includes(Pages.LOGIN)) {
        return Pages.LOGIN;
      }
      return Pages.SORRY;
    },
  },
];
